import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import React, { useCallback, useMemo, useState } from 'react';

import { JobInteraction } from './useListJobActions';
import { ActionPermissions, CustomPermission , FormObject } from '../../@types';
import { HttpMethod } from '../../api/http-client';
import { submitFormsDataWithOriginalPayload } from '../../api/interactions';
import { isUserHasPermission } from '../../configs/permissions';
import { useGetServiceById } from '../../queries/service-query';
import { FormInterceptor } from '../Services/UpdateService/ServiceActionBarFragment/FormInterceptor';

export interface InteractionDialogProps {
  interactions: JobInteraction[];
  permissions?: CustomPermission[];
  onComplete: () => void;
  onCancel: () => void;
}

/**
 * Shows interaction forms that were triggered during a status change.
 * Groups forms by job so the user can see which forms belong to which job.
 */
export const InteractionDialog: React.FC<InteractionDialogProps> = ({
  interactions,
  permissions,
  onComplete,
  onCancel,
}) => {
  const open = interactions.length > 0;
  const [formState, setFormState] = useState<Map<number, FormObject[]>>(new Map());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Initialize form state when interactions change
  React.useEffect(() => {
    if (interactions.length > 0) {
      const map = new Map<number, FormObject[]>();
      for (const job of interactions) map.set(job.serviceId, [...job.forms]);
      setFormState(map);
      setError('');
    }
  }, [interactions]);

  // Fetch service data for each job (needed by FormInterceptor)
  const serviceIds = useMemo(() => interactions.map((j) => j.serviceId), [interactions]);
  const firstServiceQuery = useGetServiceById(serviceIds[0] ?? 0);

  const canBypass = isUserHasPermission(
    ActionPermissions.Configure_Interactions_Bypass_Mandatory,
    permissions,
  );

  const totalForms = useMemo(
    () => interactions.reduce((sum, j) => sum + j.forms.length, 0),
    [interactions],
  );
  const mandatoryCount = useMemo(
    () => interactions.reduce((sum, j) => sum + j.forms.filter((f) => f.isMandatory).length, 0),
    [interactions],
  );

  const updateForm = useCallback((serviceId: number, idx: number, form: FormObject) => {
    setFormState((prev) => {
      const next = new Map(prev);
      const forms = [...(next.get(serviceId) ?? [])];
      forms[idx] = form;
      next.set(serviceId, forms);
      return next;
    });
    setError('');
  }, []);

  const handleSubmit = useCallback(async () => {
    setError('');
    setSubmitting(true);
    try {
      for (const job of interactions) {
        const forms = formState.get(job.serviceId) ?? job.forms;
        await submitFormsDataWithOriginalPayload(
          job.originalUrl,
          HttpMethod.Put,
          { ...job.originalPayload, byPassInteractionCheck: true, interactionForms: forms, actionTriggers: [] },
        );
      }
      setFormState(new Map());
      onComplete();
    } catch {
      setError('Failed to submit interaction forms');
    } finally {
      setSubmitting(false);
    }
  }, [interactions, formState, onComplete]);

  const handleSkip = useCallback(() => {
    if (!canBypass && mandatoryCount > 0) {
      setError('You do not have permission to skip mandatory interactions');
      return;
    }
    setFormState(new Map());
    onComplete();
  }, [canBypass, mandatoryCount, onComplete]);

  if (!open) return null;

  const isBulk = interactions.length > 1;
  const title = isBulk
    ? `Interaction Forms — ${interactions.length} jobs`
    : `Interaction Forms — Job #${interactions[0].serviceId}`;

  return (
    <Dialog open={open} onClose={handleSkip} maxWidth="md" fullWidth aria-label={title}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {title}
        {mandatoryCount > 0 && (
          <Chip label={`${mandatoryCount} mandatory`} size="small"
            sx={{ height: 20, fontSize: '10px', bgcolor: 'warning.main', color: 'common.white' }} />
        )}
        <Typography sx={{ fontSize: '12px', color: 'text.secondary', ml: 'auto' }}>
          {totalForms} form{totalForms !== 1 && 's'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {interactions.map((job) => {
          const forms = formState.get(job.serviceId) ?? job.forms;
          return (
            <Box key={job.serviceId} sx={{ mb: 2 }}>
              {isBulk && (
                <Typography sx={{ fontSize: '13px', fontWeight: 700, mb: 1, pb: 0.5, borderBottom: '1px solid', borderColor: 'v4.border' }}>
                  Job #{job.serviceId}
                </Typography>
              )}
              {forms.map((form, idx) => (
                <Box key={form.id ?? idx} sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: 'v4.border', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{form.title}</Typography>
                    {form.isMandatory && (
                      <Chip label="Mandatory" size="small"
                        sx={{ height: 18, fontSize: '9px', bgcolor: 'warning.main', color: 'common.white' }} />
                    )}
                  </Box>
                  {firstServiceQuery.data ? (
                    <FormInterceptor
                      service={firstServiceQuery.data}
                      value={form}
                      onChange={(updated) => { if (updated) updateForm(job.serviceId, idx, updated); }}
                      onDamagedPartSelect={() => { /* noop — not applicable on list page */ }}
                      onAddForm={() => { /* noop — not applicable on list page */ }}
                      forms={forms}
                    />
                  ) : (
                    <CircularProgress size={20} />
                  )}
                </Box>
              ))}
            </Box>
          );
        })}
        {error && <Typography sx={{ fontSize: '12px', color: 'error.main', mt: 1 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          Back
        </Button>
        <Box sx={{ flex: 1 }} />
        {canBypass && (
          <Button onClick={handleSkip} disabled={submitting} sx={{ color: 'text.secondary' }}>Skip</Button>
        )}
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}
          sx={{ bgcolor: 'v4.accent', '&:hover': { bgcolor: 'v4.accent', opacity: 0.9 } }}
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
